import { Modal } from 'bootstrap';
import tableNotification from '../components/tableNotification';

const CONTAINER_ID = 'pending-value-requests';
const NOTIFY_TIME = 4000;

// ── Scroll lock helpers ────────────────────────────────────────────────────
function lockScroll() {
  const scrollY = window.scrollY;
  document.documentElement.style.overflow = 'hidden';
  document.body.style.overflow = 'hidden';
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.width = '100%';
  document.body.dataset.scrollY = scrollY;
}

function unlockScroll() {
  const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);
  document.documentElement.style.overflow = '';
  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.width = '';
  delete document.body.dataset.scrollY;
  window.scrollTo(0, scrollY);
}

// ── Title mapping helper (matches ListValueRequestModal) ───────────────────
function friendlyLabel(val) {
  if (val === '__empty') {
    return '[None]';
  }

  if (val === '__none_selected') {
    return '[None Selected]';
  }

  if (val === '__manual') {
    return '[Manual Input]';
  }

  if (val && val.startsWith('__autofill_from_')) {
    const raw = val.replace('__autofill_from_', '');
    const spaced = raw.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ');
    const titled = spaced.replace(/\b\w/g, c => c.toUpperCase());

    return 'Autofill from ' + titled;
  }

  return val;
}

// ── Dependency-column resolution (matches ListValueRequestModal's dependencyPair) ──────────
// `pair.parentSide` / `pair.childSide` only ever describe the OTHER column in the relation —
// either side of a "New dependencies" pair can instead be this very column's own values, which
// `pair.selfSide` provides. Exactly one of parentSide/childSide is ever set (the pair-columns API
// nulls both when a column has more than one relation), and the missing side is filled in with
// selfSide.
function resolveDependencyColumns(pair) {
  if (pair.childSide) {
    // this column is itself a parent → its own values act as the "parent" side
    return { parentColumn: pair.selfSide || null, childColumn: pair.childSide };
  }

  if (pair.parentSide) {
    // this column has a parent → its own values act as the "child" side
    return { parentColumn: pair.parentSide, childColumn: pair.selfSide || null };
  }

  return { parentColumn: null, childColumn: null };
}

// ── Tooltip helper (matches ListValueRequestModal style) ───────────────────
let activeTooltip = null;

function createTooltipInput(input, tooltipTitle) {
  const showTooltip = () => {
    if (!tooltipTitle) {
      return;
    }

    hideTooltip();

    const rect = input.getBoundingClientRect();
    const tooltip = document.createElement('div');
    tooltip.className = 'j-custom-tooltip';
    tooltip.style.cssText = `
      position: fixed;
      top: ${rect.top - 8}px;
      left: ${rect.left + rect.width / 2}px;
      transform: translate(-50%, -100%);
      z-index: 9999;
      pointer-events: none;
      white-space: nowrap;
    `;
    tooltip.innerHTML = `
      <div style="background:#000;color:#fff;padding:4px 8px;border-radius:4px;font-size:0.875rem;line-height:1.4;text-align:center;">
        ${tooltipTitle}
      </div>
      <div style="width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-top:6px solid #000;margin:0 auto;"></div>
    `;
    document.body.appendChild(tooltip);
    activeTooltip = tooltip;
  };

  const hideTooltip = () => {
    if (activeTooltip) {
      activeTooltip.remove();
      activeTooltip = null;
    }
  };

  input.addEventListener('focus', showTooltip);
  input.addEventListener('blur', hideTooltip);
  input.addEventListener('mouseenter', showTooltip);
  input.addEventListener('mouseleave', hideTooltip);
}

// ── Decision request helper ─────────────────────────────────────────────────
function postDecision(id, body) {
  return fetch('/api/v1/column/list-value-request/' + id + '/decision', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    body: JSON.stringify(body),
  }).then(r =>
    r
      .json()
      .catch(() => ({}))
      .then(data => ({ ok: r.ok, data }))
  );
}

// Selectize (wired in global.js) owns the visible control, so a plain `select.value = x`
// wouldn't be reflected — go through its API when it is there.
function setSelectValue(select, value) {
  if (select.selectize) {
    select.selectize.setValue(value, true);
  } else {
    select.value = value;
  }
}

const DECISION_MODAL_TITLES = {
  done: 'Approve Value Request',
  closed: 'Reject Value Request',
  edit: 'Edit Note / Contact',
};

export default {
  pendingClarifyId: null,
  pendingEditId: null,
  pendingEditRow: null,

  // Decision flow pending state (Approve/Reject buttons, status → done/closed, re-edit pencil)
  pendingDecisionId: null,
  pendingDecisionSelect: null,
  pendingDecisionRevertValue: null,
  pendingDecisionStatus: null,
  pendingDecisionNotes: null,
  pendingDecisionResponsibleId: null,
  decisionSaved: false,
  decisionContinueToReject: false,

  init() {
    this.initModalStacking();
    this.initEditModal();
    this.initClarify();
    this.initApprove();
    this.initReject();
    this.initDecision();
  },

  showNotice(state, message) {
    tableNotification.showMessageByTime(CONTAINER_ID, message, state, NOTIFY_TIME);
  },

  // ── Modal stacking fix ───────────────────────────────────────────────────

  initModalStacking() {
    const MODAL_Z = '1200';
    const BACKDROP_Z = '1190';

    ['editRequestModal', 'clarifyModal', 'rejectModal', 'decisionModal'].forEach(id => {
      const el = document.getElementById(id);

      if (!el) {
        return;
      }

      // Move immediately so no stacking-context from .content-wrapper interferes
      if (el.parentElement !== document.body) {
        document.body.appendChild(el);
      }

      // Force z-index via inline style (overrides any CSS, stays above sidebar z-index:1100)
      el.style.zIndex = MODAL_Z;

      // Ensure modal dialog is centered
      const dialog = el.querySelector('.modal-dialog');

      if (dialog && !dialog.classList.contains('modal-dialog-centered')) {
        dialog.classList.add('modal-dialog-centered');
      }

      // Ensure modal has proper centering styles
      el.style.display = '';
      el.style.alignItems = '';
      el.style.justifyContent = '';

      // When the modal opens, also fix the backdrop z-index and lock scroll
      el.addEventListener('show.bs.modal', () => {
        // Force proper centering display
        el.style.display = 'flex';
        el.style.alignItems = 'center';
        el.style.justifyContent = 'center';
      });

      // When the modal opens, also fix the backdrop z-index and lock scroll
      el.addEventListener('shown.bs.modal', () => {
        document.querySelectorAll('.modal-backdrop').forEach(backdrop => {
          backdrop.style.zIndex = BACKDROP_Z;
        });
        lockScroll();
      });

      // When the modal closes, unlock scroll
      el.addEventListener('hidden.bs.modal', () => {
        unlockScroll();
      });
    });
  },

  initEditModal() {
    const editModalEl = document.getElementById('editRequestModal');

    if (!editModalEl) {
      return;
    }

    const editModal = new Modal(editModalEl);
    const editModalDialog = document.getElementById('editRequestModalDialog');
    const editModalBody = document.getElementById('editModalBody');
    const editSaveBtn = document.getElementById('editSaveBtn');

    // ── Open modal on edit-button click ──────────────────────────────────
    document.querySelectorAll('.j-edit-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const row = btn.closest('tr');
        const id = btn.dataset.id;

        this.pendingEditId = id;
        this.pendingEditRow = row;

        const itemValue = row.dataset.value || row.querySelector('.j-display-value').textContent.trim();
        const shortVal = row.dataset.shortValue || '';
        const extendedValidation = row.dataset.extendedValidation === 'true';

        let pair = {};

        try {
          pair = JSON.parse(row.dataset.pair || '{}');
        } catch (_e) {
          console.error(_e);
        }

        const savedParentId = row.dataset.parentValueId || '';
        let savedChildIds = [];

        try {
          savedChildIds = JSON.parse(row.dataset.childValueIds || '[]');
        } catch (_e) {
          console.error(_e);
        }

        // Determine which columns to show (only filled)
        const showValue = !!itemValue;
        // Short value is optional at request time, so always show the field for value-carrying
        // requests (not just when already filled) — lets the admin add one before approving.
        const showShort = showValue;

        const { parentColumn, childColumn } = resolveDependencyColumns(pair);

        const hasParentValue = !!savedParentId;
        const hasChildValues = savedChildIds.length > 0;
        const parentOptionsAvailable = !!(parentColumn && parentColumn.values && parentColumn.values.length > 0);
        const childOptionsAvailable = !!(childColumn && childColumn.values && childColumn.values.length > 0);

        const showParentSelect = hasParentValue && parentOptionsAvailable;
        // Fallback for an edge case that should no longer happen now selfSide is always sent —
        // kept so a value removed from its column after the request was made (or stale data from
        // before this fix shipped) still shows something instead of silently vanishing, and Save
        // still never wipes it.
        const showParentRaw = hasParentValue && !parentOptionsAvailable;
        const showChildSelect = hasChildValues && childOptionsAvailable;
        const showChildRaw = hasChildValues && !childOptionsAvailable;

        const showParentColumn = showParentSelect || showParentRaw;
        const showChildColumn = showChildSelect || showChildRaw;

        // Build table
        editModalBody.innerHTML = '';

        const table = document.createElement('table');
        table.className = 'table align-middle mb-0';

        // ── thead ──
        const thead = document.createElement('thead');
        thead.className = 'table-light';
        const headerRow = document.createElement('tr');

        // Value header (only when filled)
        if (showValue) {
          const thValue = document.createElement('th');
          thValue.style.minWidth = '320px';
          thValue.innerHTML =
            '<div>Item value <span class="text-danger">*</span></div>' +
            '<div class="font-weight-normal">(value to be selected in the drop down)</div>';
          headerRow.appendChild(thValue);
        }

        if (showShort) {
          const thShort = document.createElement('th');
          thShort.style.minWidth = '350px';
          thShort.innerHTML =
            '<div>Short Value <span class="text-danger">*</span></div>' +
            '<div class="font-weight-normal">(value which will appear in the generated name)</div>';
          headerRow.appendChild(thShort);
        }

        if (showParentColumn) {
          const thParent = document.createElement('th');
          thParent.style.minWidth = '200px';
          thParent.innerHTML = showParentSelect
            ? parentColumn.name + ' <small class="text-muted">(parent)</small>'
            : 'Parent value <small class="text-muted">(id only — column data unavailable)</small>';
          headerRow.appendChild(thParent);
        }

        if (showChildColumn) {
          const thChild = document.createElement('th');
          thChild.style.minWidth = '200px';
          thChild.innerHTML = showChildSelect
            ? childColumn.name
            : 'Child values <small class="text-muted">(id only — column data unavailable)</small>';
          headerRow.appendChild(thChild);
        }

        thead.appendChild(headerRow);
        table.appendChild(thead);

        // ── tbody ──
        const tbody = document.createElement('tbody');
        const formRow = document.createElement('tr');

        // Value input (only when filled)
        if (showValue) {
          const tdValue = document.createElement('td');
          const inputValue = document.createElement('input');
          inputValue.type = 'text';
          inputValue.className = 'form-control';
          inputValue.id = 'editValue';
          inputValue.maxLength = 200;
          inputValue.placeholder = 'Enter item value';
          inputValue.value = itemValue;
          createTooltipInput(inputValue, "This value should start from letter, digit or '{'");
          tdValue.appendChild(inputValue);
          formRow.appendChild(tdValue);
        }

        // Short Value input
        if (showShort) {
          const tdShort = document.createElement('td');
          const inputShort = document.createElement('input');
          inputShort.type = 'text';
          inputShort.className = 'form-control';
          inputShort.id = 'editShortValue';
          inputShort.maxLength = 60;
          inputShort.placeholder = 'Enter short value';
          inputShort.style.textTransform = 'uppercase';
          inputShort.value = shortVal;

          const shortTooltip = extendedValidation
            ? "This value should start from letter, digit, or '{', or '_' and can have more letters, digits, '-', '_', '.', and '{}'"
            : "This value should start from letter, digit, or '{' and can have more letters, digits, '-', '_', '.', and '{}'";

          createTooltipInput(inputShort, shortTooltip);

          tdShort.appendChild(inputShort);
          formRow.appendChild(tdShort);
        }

        // Parent cell — editable select when we have the column's values to label it with,
        // otherwise a read-only fallback that just preserves the id on save.
        if (showParentColumn) {
          const tdParent = document.createElement('td');

          if (showParentSelect) {
            const sel = document.createElement('select');
            sel.className = 'form-control';
            sel.id = 'editParentValue';

            const emptyOpt = document.createElement('option');
            emptyOpt.value = '';
            emptyOpt.textContent = '— none —';
            sel.appendChild(emptyOpt);

            parentColumn.values.forEach(v => {
              const opt = document.createElement('option');
              opt.value = v.id;
              opt.textContent = friendlyLabel(v.value);

              if (v.id === savedParentId) {
                opt.selected = true;
              }

              sel.appendChild(opt);
            });

            tdParent.appendChild(sel);
          } else {
            const raw = document.createElement('input');
            raw.type = 'text';
            raw.className = 'form-control';
            raw.id = 'editParentValueRaw';
            raw.readOnly = true;
            raw.disabled = true;
            raw.value = savedParentId;
            raw.dataset.value = savedParentId;
            raw.title =
              "This value's column data isn't available here, so it can't be relabeled or changed — it is kept as-is.";
            tdParent.appendChild(raw);
          }

          formRow.appendChild(tdParent);
        }

        // Child cell — editable checkboxes when we have the column's values to label them with,
        // otherwise a read-only fallback that just preserves the ids on save.
        if (showChildColumn) {
          const tdChild = document.createElement('td');

          if (showChildSelect) {
            const container = document.createElement('div');
            container.id = 'editChildCheckboxes';
            container.style.cssText =
              'max-height:120px; overflow-y:auto; border:1px solid #dee2e6; border-radius:4px; padding:4px 8px;';

            childColumn.values.forEach(v => {
              const chkDiv = document.createElement('div');
              chkDiv.className = 'form-check form-check-sm';

              const chk = document.createElement('input');
              chk.className = 'form-check-input j-edit-child-chk';
              chk.type = 'checkbox';
              chk.value = v.id;
              chk.id = 'editChild_' + v.id;

              if (savedChildIds.indexOf(v.id) !== -1) {
                chk.checked = true;
              }

              const chkLabel = document.createElement('label');
              chkLabel.className = 'form-check-label small';
              chkLabel.setAttribute('for', chk.id);
              chkLabel.textContent = friendlyLabel(v.value);

              chkDiv.appendChild(chk);
              chkDiv.appendChild(chkLabel);
              container.appendChild(chkDiv);
            });

            tdChild.appendChild(container);
          } else {
            const raw = document.createElement('input');
            raw.type = 'text';
            raw.className = 'form-control';
            raw.id = 'editChildValueRaw';
            raw.readOnly = true;
            raw.disabled = true;
            raw.value = savedChildIds.join(', ');
            raw.dataset.value = JSON.stringify(savedChildIds);
            raw.title =
              "These values' column data isn't available here, so they can't be relabeled or changed — they are kept as-is.";
            tdChild.appendChild(raw);
          }

          formRow.appendChild(tdChild);
        }

        tbody.appendChild(formRow);
        table.appendChild(tbody);
        editModalBody.appendChild(table);

        // Dynamic width like ListValueRequestModal
        editModalDialog.style.maxWidth = showParentColumn && showChildColumn ? 'calc(100vw - 5rem)' : '1000px';

        editModal.show();
      });
    });

    // ── Save button inside modal ─────────────────────────────────────────
    editSaveBtn.addEventListener('click', () => {
      const id = this.pendingEditId;
      const row = this.pendingEditRow;

      if (!id || !row) {
        return;
      }

      const editValueInput = document.getElementById('editValue');
      const editShortInput = document.getElementById('editShortValue');
      const value = editValueInput ? editValueInput.value.trim() : row.dataset.value || '';
      const shortValue = editShortInput ? editShortInput.value.trim().toUpperCase() : row.dataset.shortValue || '';

      let pair = {};

      try {
        pair = JSON.parse(row.dataset.pair || '{}');
      } catch (_e) {
        console.error(_e);
      }

      const body = {};

      // Only include value/short_value if they have data
      if (value) {
        body.value = value;
      }

      if (shortValue) {
        body.short_value = shortValue;
      }

      // Parent value — an editable select sends the admin's current choice (including an
      // explicit clear-to-none); the read-only fallback (rendered when we don't have the
      // column's values to label it with) round-trips the existing id untouched. Either way,
      // never omit the key when this row actually had a parent value — omitting it is what
      // used to make the server null it out on save.
      const parentSel = document.getElementById('editParentValue');
      const parentRaw = document.getElementById('editParentValueRaw');

      if (parentSel) {
        body.selected_parent_value_id = parentSel.value || null;
      } else if (parentRaw) {
        body.selected_parent_value_id = parentRaw.dataset.value || null;
      }

      // Child values — same preserve-if-not-editable rule as the parent value above.
      const childContainer = document.getElementById('editChildCheckboxes');
      const childRaw = document.getElementById('editChildValueRaw');

      if (childContainer) {
        body.selected_child_value_ids = Array.from(editModalBody.querySelectorAll('.j-edit-child-chk:checked')).map(
          chk => chk.value
        );
      } else if (childRaw) {
        try {
          body.selected_child_value_ids = JSON.parse(childRaw.dataset.value || '[]');
        } catch (_e) {
          body.selected_child_value_ids = [];
        }
      }

      editSaveBtn.disabled = true;

      fetch('/api/v1/column/list-value-request/' + id + '/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify(body),
      })
        .then(r =>
          r
            .json()
            .catch(() => ({}))
            .then(data => ({ ok: r.ok, data }))
        )
        .then(({ ok, data }) => {
          if (!ok) {
            this.showNotice('error', data.error || 'Failed to save changes.');
            editSaveBtn.disabled = false;
            return;
          }

          // Update display values in the table row
          row.querySelector('.j-display-value').textContent = data.value;
          row.querySelector('.j-display-short').textContent = data.shortValue || '—';
          row.dataset.value = data.value || '';
          row.dataset.shortValue = data.shortValue || '';

          const pairDisplay = row.querySelector('.j-display-pair');

          if (pairDisplay) {
            const { parentColumn, childColumn } = resolveDependencyColumns(pair);
            const parts = [];

            if (data.parentValueId) {
              const parentVals = (parentColumn && parentColumn.values) || [];
              const matchedParent = parentVals.find(v => v.id === data.parentValueId);
              const label = matchedParent ? friendlyLabel(matchedParent.value) : data.parentValueId;
              parts.push((parentColumn ? parentColumn.name : 'Parent value') + ': ' + label);
            }

            if (data.childValueIds && data.childValueIds.length > 0) {
              const childVals = (childColumn && childColumn.values) || [];
              const names = data.childValueIds.map(cid => {
                const m = childVals.find(v => v.id === cid);

                return m ? friendlyLabel(m.value) : cid;
              });
              parts.push((childColumn ? childColumn.name : 'Child values') + ': ' + names.join(', '));
            }

            pairDisplay.textContent = parts.length > 0 ? parts.join(' | ') : '—';
            row.dataset.parentValueId = data.parentValueId || '';
            row.dataset.childValueIds = JSON.stringify(data.childValueIds || []);
          }

          editModal.hide();
          this.pendingEditId = null;
          this.pendingEditRow = null;
          editSaveBtn.disabled = false;

          this.showNotice('success', 'Saved.');
        })
        .catch(() => {
          this.showNotice('error', 'Failed to save changes.');
          editSaveBtn.disabled = false;
        });
    });
  },

  // ── Clarify ──────────────────────────────────────────────────────────────

  initClarify() {
    document.querySelectorAll('.j-clarify-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        this.pendingClarifyId = btn.dataset.requestId;
        document.getElementById('clarifyComment').value = '';
      });
    });

    const clarifyConfirmBtn = document.getElementById('clarifyConfirmBtn');

    if (!clarifyConfirmBtn) {
      return;
    }

    clarifyConfirmBtn.addEventListener('click', () => {
      if (!this.pendingClarifyId) {
        return;
      }

      const comment = document.getElementById('clarifyComment').value.trim();

      if (!comment) {
        document.getElementById('clarifyComment').classList.add('is-invalid');
        return;
      }

      document.getElementById('clarifyComment').classList.remove('is-invalid');
      clarifyConfirmBtn.disabled = true;

      fetch('/api/v1/column/list-value-request/' + this.pendingClarifyId + '/clarify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: 'comment=' + encodeURIComponent(comment),
      })
        .then(r => r.json())
        .then(data => {
          const modal = Modal.getInstance(document.getElementById('clarifyModal'));

          if (modal) {
            modal.hide();
          }

          this.showNotice('success', data.message || 'Clarification sent.');
          this.pendingClarifyId = null;
          clarifyConfirmBtn.disabled = false;
        })
        .catch(() => {
          this.showNotice('error', 'Error sending clarification.');
          clarifyConfirmBtn.disabled = false;
        });
    });
  },

  // ── Approve / Reject buttons ─────────────────────────────────────────────
  // Both buttons are shortcuts for the status select: Approve == switching to "done", Reject ==
  // switching to "closed". They run through the same modal(s) and the same /decision endpoint,
  // so there is a single code path for finalizing a request.

  initApprove() {
    document.querySelectorAll('.j-approve-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        this.openDecisionModal(btn.dataset.id, btn.closest('tr'), 'done', null);
      });
    });
  },

  initReject() {
    document.querySelectorAll('.j-reject-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        this.openDecisionModal(btn.dataset.id, btn.closest('tr'), 'closed', null);
      });
    });
  },

  // ── Decision flow ────────────────────────────────────────────────────────
  //   done   : decision modal (note + LVMH contact) → POST → reload
  //   closed : decision modal (note + LVMH contact) → reject modal (comment for requester) → POST → reload
  //   null   : re-edit pencil on an already done/closed row — decision modal → POST → reload
  // Cancelling at any step reverts the status select (when the flow was started from it).

  // `newStatus` is the status being switched to, or null in edit mode (no status is sent).
  // `select` is the status <select> the flow was started from, or null for the buttons.
  openDecisionModal(id, row, newStatus, select) {
    this.pendingDecisionId = id;
    this.pendingDecisionSelect = select || null;
    this.pendingDecisionRevertValue = select ? select.dataset.prevValue : null;
    this.pendingDecisionStatus = newStatus;
    this.pendingDecisionNotes = null;
    this.pendingDecisionResponsibleId = null;
    this.decisionSaved = false;
    this.decisionContinueToReject = false;

    const notesEl = document.getElementById('decisionNotes');
    const responsibleEl = document.getElementById('decisionResponsible');

    notesEl.value = (row && row.dataset.adminNotes) || '';
    responsibleEl.value = (row && row.dataset.decisionResponsibleId) || '';
    responsibleEl.classList.remove('is-invalid');

    document.getElementById('decisionModalLabel').textContent = DECISION_MODAL_TITLES[newStatus || 'edit'];

    const modalEl = document.getElementById('decisionModal');
    const modal = Modal.getInstance(modalEl) || new Modal(modalEl);
    modal.show();
  },

  resetDecisionState() {
    this.pendingDecisionId = null;
    this.pendingDecisionSelect = null;
    this.pendingDecisionRevertValue = null;
    this.pendingDecisionStatus = null;
    this.pendingDecisionNotes = null;
    this.pendingDecisionResponsibleId = null;
    this.decisionSaved = false;
    this.decisionContinueToReject = false;
  },

  revertDecisionSelect() {
    if (this.pendingDecisionSelect && this.pendingDecisionRevertValue !== null) {
      setSelectValue(this.pendingDecisionSelect, this.pendingDecisionRevertValue);
    }
  },

  // Sends the collected decision. `comment` is only passed on the reject path. On success the page
  // is reloaded so the row (status, date, actions, pencil) is rendered from the persisted state
  // instead of being patched client-side. On failure the modal is closed (which also reverts the
  // select) so the notice in the card header is visible.
  submitDecision(modalEl, button, comment) {
    const body = {
      admin_notes: this.pendingDecisionNotes,
      decision_responsible_id: this.pendingDecisionResponsibleId,
    };

    if (this.pendingDecisionStatus !== null) {
      body.status = this.pendingDecisionStatus;
    }

    if (comment !== undefined) {
      body.comment = comment;
    }

    button.disabled = true;

    const fail = message => {
      button.disabled = false;

      const modal = Modal.getInstance(modalEl);

      if (modal) {
        modal.hide();
      }

      this.showNotice('error', message);
    };

    postDecision(this.pendingDecisionId, body)
      .then(({ ok, data }) => {
        if (!ok) {
          fail(data.error || data.message || 'Error saving decision.');
          return;
        }

        this.decisionSaved = true;
        window.location.reload();
      })
      .catch(() => {
        fail('Error saving decision.');
      });
  },

  initDecision() {
    const self = this;

    document.querySelectorAll('.j-status-select').forEach(select => {
      select.dataset.prevValue = select.value;
    });

    // Delegated on document, not bound per-element: Selectize (data-selectize-init, wired in
    // global.js) initializes AFTER this runs and internally detaches/reattaches each <select>
    // while setting it up (selectize.js's replaceWith dance), which wipes any jQuery handler
    // bound directly to the element beforehand. It also syncs its selection back via jQuery's
    // own trigger('change'), which never reaches a plain addEventListener('change'). Delegation
    // on a node Selectize never touches survives both problems.
    $(document).on('change', '.j-status-select', function () {
      const select = this;
      const id = select.dataset.id;
      const row = select.closest('tr');
      const newStatus = select.value;

      if (newStatus === select.dataset.prevValue) {
        return;
      }

      if (newStatus === 'done' || newStatus === 'closed') {
        self.openDecisionModal(id, row, newStatus, select);
        return;
      }

      postDecision(id, { status: newStatus })
        .then(({ ok, data }) => {
          if (!ok) {
            setSelectValue(select, select.dataset.prevValue);
            self.showNotice('error', data.error || data.message || 'Error updating status.');
            return;
          }

          select.dataset.prevValue = newStatus;
          self.showNotice('success', 'Status updated.');
        })
        .catch(() => {
          setSelectValue(select, select.dataset.prevValue);
          self.showNotice('error', 'Error updating status.');
        });
    });

    document.querySelectorAll('.j-edit-decision-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        this.openDecisionModal(btn.dataset.id, btn.closest('tr'), null, null);
      });
    });

    const decisionModalEl = document.getElementById('decisionModal');
    const rejectModalEl = document.getElementById('rejectModal');

    if (!decisionModalEl || !rejectModalEl) {
      return;
    }

    const decisionConfirmBtn = document.getElementById('decisionConfirmBtn');
    const rejectConfirmBtn = document.getElementById('rejectConfirmBtn');
    const rejectCommentEl = document.getElementById('rejectComment');

    // Step 1 — note + LVMH contact
    decisionConfirmBtn.addEventListener('click', () => {
      if (!this.pendingDecisionId) {
        return;
      }

      const responsibleEl = document.getElementById('decisionResponsible');
      const responsibleId = responsibleEl.value;

      responsibleEl.classList.toggle('is-invalid', !responsibleId);

      if (!responsibleId) {
        return;
      }

      this.pendingDecisionNotes = document.getElementById('decisionNotes').value.trim();
      this.pendingDecisionResponsibleId = responsibleId;

      if (this.pendingDecisionStatus === 'closed') {
        // Rejecting: continue to the comment step once this modal has finished hiding —
        // Bootstrap can't show a second modal while the first is still animating out.
        this.decisionContinueToReject = true;
        Modal.getInstance(decisionModalEl).hide();
        return;
      }

      this.submitDecision(decisionModalEl, decisionConfirmBtn);
    });

    decisionModalEl.addEventListener('hidden.bs.modal', () => {
      if (this.decisionContinueToReject) {
        this.decisionContinueToReject = false;
        rejectCommentEl.value = '';
        (Modal.getInstance(rejectModalEl) || new Modal(rejectModalEl)).show();
        return;
      }

      if (!this.decisionSaved) {
        this.revertDecisionSelect();
      }

      this.resetDecisionState();
    });

    // Step 2 (reject only) — comment emailed to the requester
    rejectConfirmBtn.addEventListener('click', () => {
      if (!this.pendingDecisionId) {
        return;
      }

      this.submitDecision(rejectModalEl, rejectConfirmBtn, rejectCommentEl.value.trim());
    });

    rejectModalEl.addEventListener('hidden.bs.modal', () => {
      if (!this.decisionSaved) {
        this.revertDecisionSelect();
      }

      this.resetDecisionState();
    });
  },
};
